import { useParams } from "@tanstack/react-router";
import dayjs from "dayjs";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UNIT_DAY } from "@/constants";
import { StepAdditionalDetails } from "@/features/create-event/components/StepAdditionalDetails";
import { StepEventDetails } from "@/features/create-event/components/StepEventDetails";
import { StepIsPublic } from "@/features/create-event/components/StepIsPublic";
import { StepReview } from "@/features/create-event/components/StepReview";
import { StepSuccess } from "@/features/create-event/components/StepSuccess";
import type { EventFormData } from "@/features/study-group-events/type";
import { useCreateEvent } from "@/hooks/useCreateEvent";
import { useCreateEventReminders } from "@/hooks/useCreateEventReminders";
import { usePropagateReminders } from "@/hooks/usePropagateReminders";
import {
  PARTICIPATION_STATE_ACCEPTED,
  useRespondToEvent,
} from "@/hooks/useRespondToEvent";
import { useStudyGroupInfo } from "@/hooks/useStudyGroupInfo";
import i18n from "@/lib/i18n";
import { eventsApi } from "@/lib/humhub-api/events";
import type { EventApiResponse } from "@/lib/humhub-api/types";
import { dedupeReminderItems } from "@/lib/utils/event/form-initializer";
import { validateRecurrenceRule } from "@/lib/utils/event/recurrence";
import { getCurrentBrowserTimeZone } from "@/lib/utils/timeZone";
import {
  transformFormToCreateApiRequest,
  transformFormToDraftApiRequest,
} from "@/lib/utils/eventUtils";

const buildDefaultFormData = (initialDate?: Date): EventFormData => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDate = initialDate ?? tomorrow;

  const defaultDateStr = dayjs(defaultDate).format("YYYY-MM-DD");
  const defaultTimeZone = getCurrentBrowserTimeZone();
  return {
    title: "",
    language: i18n.language,
    isAllDay: false,
    timeZone: defaultTimeZone,
    startDate: defaultDateStr,
    startTime: "09:00",
    endDate: defaultDateStr,
    endTime: "10:00",
    channel: "",
    location: "",
    description: "",
    reminders: [{ unit: UNIT_DAY, value: 1 }],
    files: [],
    is_public: 0,
    is_draft: 0,
    online_location: "",
    location_type: 1,
    hidden: 0,
    recurrence: null,
    scope: "single",
  };
};

interface EventCreateContentProps {
  onSuccess?: () => void;
  onCompletionStateChange?: (completed: boolean) => void;
  initialDate?: Date;
  initialFormData?: EventFormData;
}

export const EventCreateContent: React.FC<EventCreateContentProps> = ({
  onSuccess,
  onCompletionStateChange,
  initialDate,
  initialFormData,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [createdEvent, setCreatedEvent] = useState<EventApiResponse | null>(
    null,
  );
  const { createEvent, isLoading } = useCreateEvent();
  const params = useParams({ from: "/study-group/$study-group-id" });
  const studyGroupIdParam = params["study-group-id"];
  const studyGroupId = studyGroupIdParam ? Number(studyGroupIdParam) : null;
  const { studyGroupInfo } = useStudyGroupInfo(studyGroupId);

  const defaultFormData = buildDefaultFormData(initialDate);
  const [formData, setFormData] = useState<EventFormData>(
    initialFormData ?? defaultFormData,
  );

  const { isLoading: remindersLoading, createReminders } =
    useCreateEventReminders({});
  const { propagateReminders, isLoading: propagateLoading } =
    usePropagateReminders();
  const { respond, isLoading: respondLoading } = useRespondToEvent();
  const [isHydratingCreatedEvent, setIsHydratingCreatedEvent] = useState(false);
  const [recurrenceErrors, setRecurrenceErrors] = useState<string[]>([]);

  useEffect(() => {
    const result = validateRecurrenceRule(
      formData.recurrence,
      formData.startDate,
      formData.endDate,
      t,
      undefined,
    );
    setRecurrenceErrors(result.errors);
  }, [formData.recurrence, formData.startDate, formData.endDate, t]);

  useEffect(() => {
    setStep(1);
  }, []);

  useEffect(() => {
    onCompletionStateChange?.(false);
  }, [onCompletionStateChange]);

  // イベント作成後にリマインダーを作成する関数
  const createRemindersForEvent = async (eventId: number) => {
    let createdAny = false;

    if (formData.reminders && formData.reminders.length > 0) {
      try {
        const uniqueReminders = dedupeReminderItems(formData.reminders);
        const reminderData = uniqueReminders.map((reminder) => ({
          unit: reminder.unit,
          value: reminder.value,
        }));

        await createReminders(eventId, reminderData);
        createdAny = true;
      } catch (error) {
        console.error(
          "Failed to create reminders in batch, falling back to individual creation:",
          error,
        );

        const uniqueReminders = dedupeReminderItems(formData.reminders);
        for (const reminder of uniqueReminders) {
          try {
            await createReminders(eventId, [
              { unit: reminder.unit, value: reminder.value },
            ]);
            createdAny = true;
          } catch (fallbackError) {
            console.error(
              `Failed to create reminder (unit: ${reminder.unit}, value: ${reminder.value}):`,
              fallbackError,
            );
          }
        }
      }
    }

    if (formData.recurrence && createdAny) {
      try {
        await propagateReminders(eventId, "all");
      } catch (propagateError) {
        console.error("Failed to propagate reminders:", propagateError);
      }
    }
  };

  const runPostCreateActions = async (
    eventId: number,
    recurrenceChildIds?: number[],
  ) => {
    await createRemindersForEvent(eventId);
    const targetEventIds = Array.from(
      new Set([eventId, ...(recurrenceChildIds ?? [])]),
    );

    for (const targetId of targetEventIds) {
      try {
        await respond(targetId, PARTICIPATION_STATE_ACCEPTED);
      } catch (error) {
        console.error(
          `Failed to auto-confirm attendance on create for event ${targetId}:`,
          error,
        );
      }
    }
  };

  const handleSaveDraft = async () => {
    const recurrenceResult = validateRecurrenceRule(
      formData.recurrence,
      formData.startDate,
      formData.endDate,
      t,
      undefined,
    );
    setRecurrenceErrors(recurrenceResult.errors);
    if (!recurrenceResult.isValid) return;

    if (!studyGroupInfo || !studyGroupInfo.contentcontainer_id) {
      console.error("No container ID available for saving draft");
      return;
    }

    try {
      const eventRequest = transformFormToDraftApiRequest(formData);
      const createdEvent = await createEvent(
        Number(studyGroupInfo.contentcontainer_id),
        eventRequest,
        formData.files,
      );

      // 下書き保存時もリマインダーを作成
      if (createdEvent?.id) {
        await createRemindersForEvent(createdEvent.id);
      }

      onSuccess?.();
    } catch (err) {
      console.error("Failed to save event as draft:", err);
    }
  };

  const handleNext = async () => {
    if (step === 4) {
      const recurrenceResult = validateRecurrenceRule(
        formData.recurrence,
        formData.startDate,
        formData.endDate,
        t,
        undefined,
      );
      setRecurrenceErrors(recurrenceResult.errors);
      if (!recurrenceResult.isValid) return;

      if (!studyGroupInfo || !studyGroupInfo.contentcontainer_id) {
        console.error("No container ID available");
        return;
      }

      try {
        const eventRequest = transformFormToCreateApiRequest(formData);
        const createdEvent = await createEvent(
          Number(studyGroupInfo.contentcontainer_id),
          eventRequest,
          formData.files,
        );

        // イベント作成後にリマインダーを作成し、同時に主催者の出席表明も行う
        if (createdEvent?.id) {
          await runPostCreateActions(
            createdEvent.id,
            createdEvent.recurrence_child_ids,
          );
        }

        let hydratedCreatedEvent = createdEvent;
        if (createdEvent?.id) {
          setIsHydratingCreatedEvent(true);
          try {
            hydratedCreatedEvent = await eventsApi.getEventById(
              createdEvent.id,
            );
          } catch (fetchError) {
            console.error(
              `Failed to fetch created event detail for event ${createdEvent.id}:`,
              fetchError,
            );
          } finally {
            setIsHydratingCreatedEvent(false);
          }
        }

        setCreatedEvent(hydratedCreatedEvent);
        onCompletionStateChange?.(true);
        setStep(5);
      } catch (err) {
        console.error("Failed to create event:", err);
      }
    } else {
      setStep((prev) => Math.min(4, prev + 1));
    }
  };

  const isProcessing =
    isLoading ||
    remindersLoading ||
    respondLoading ||
    propagateLoading ||
    isHydratingCreatedEvent;

  const updateFormData = (updates: Partial<EventFormData>) => {
    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <StepIsPublic
            formData={formData}
            onSelect={(value) =>
              setFormData((prev) => ({ ...prev, is_public: value }))
            }
            handleNext={handleNext}
            studyGroupInfo={studyGroupInfo}
            isSubmitting={isProcessing}
          />
        );
      case 2:
        return (
          <StepEventDetails
            formData={formData}
            onChange={updateFormData}
            handleNext={handleNext}
            handleSaveDraft={handleSaveDraft}
            setStep={setStep}
            isSubmitting={isProcessing}
            step={step}
            recurrenceErrors={recurrenceErrors}
          />
        );
      case 3:
        return (
          <StepAdditionalDetails
            formData={formData}
            onChange={updateFormData}
            handleNext={handleNext}
            handleSaveDraft={handleSaveDraft}
            setStep={setStep}
            isSubmitting={isProcessing}
            step={step}
          />
        );
      case 4:
        return (
          <StepReview
            formData={formData}
            handleNext={handleNext}
            handleSaveDraft={handleSaveDraft}
            setStep={setStep}
            isSubmitting={isProcessing}
          />
        );
      case 5:
        return createdEvent ? (
          <StepSuccess
            createdEvent={createdEvent}
            onClose={() => onSuccess?.()}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="flex size-full flex-col">
      <div className="min-h-0 flex-1">{renderStep()}</div>
    </div>
  );
};
